import { tokenParaApi } from '../auth/msal';

const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    readonly codigo: string,
    mensaje: string,
    readonly detalle?: unknown,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }

  get esSinPermiso() {
    return this.estado === 403;
  }

  get esNoAutenticado() {
    return this.estado === 401;
  }
}

interface CuerpoDeError {
  error?: { codigo?: string; mensaje?: string; detalle?: unknown };
}

async function pedir<T>(ruta: string, init: RequestInit = {}): Promise<T> {
  const token = await tokenParaApi();

  const respuesta = await fetch(`${BASE}/api${ruta}`, {
    ...init,
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
      authorization: `Bearer ${token}`,
    },
  });

  if (respuesta.status === 204) return undefined as T;

  if (!respuesta.ok) {
    let cuerpo: CuerpoDeError = {};
    try {
      cuerpo = (await respuesta.json()) as CuerpoDeError;
    } catch {
      /* la respuesta no era JSON */
    }
    throw new ErrorApi(
      respuesta.status,
      cuerpo.error?.codigo ?? 'error',
      cuerpo.error?.mensaje ?? `La API respondió ${respuesta.status}.`,
      cuerpo.error?.detalle,
    );
  }

  return (await respuesta.json()) as T;
}

export const api = {
  get: <T>(ruta: string) => pedir<T>(ruta),
  post: <T>(ruta: string, cuerpo: unknown) =>
    pedir<T>(ruta, { method: 'POST', body: JSON.stringify(cuerpo) }),
  put: <T>(ruta: string, cuerpo: unknown) =>
    pedir<T>(ruta, { method: 'PUT', body: JSON.stringify(cuerpo) }),
  borrar: (ruta: string) => pedir<void>(ruta, { method: 'DELETE' }),
};

/// Foto de perfil. Va por fetch y no como `<img src>` porque un `<img>` no
/// manda el header Authorization y la ruta está autenticada.
/// Devuelve null si la persona no tiene foto cargada en Entra (404).
export async function pedirFoto(usuarioId: string): Promise<Blob | null> {
  const token = await tokenParaApi();
  const respuesta = await fetch(`${BASE}/api/usuarios/${usuarioId}/foto`, {
    headers: { authorization: `Bearer ${token}` },
  });

  if (respuesta.status === 404) return null;
  if (!respuesta.ok) {
    throw new ErrorApi(respuesta.status, 'error', `La API respondió ${respuesta.status}.`);
  }
  return respuesta.blob();
}
